import { ISessionStateService } from "./i-session-state.service";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ICurrentUser } from "../../models/i-current-user";
import { ICloud } from "../../models/i-cloud";
import { IGroup } from "../../models/i-group";
import { IReceiver } from "src/app/models/i-receiver";
import { ICompany } from "../../models/i-company";
import { ILicense } from "../../models/i-license";
import { IJoinRequest } from "../../models/i-join-request";
import { TCompanyRole } from "../../models/company-role";

@Injectable()
export class SessionStateService implements ISessionStateService {
  private readonly _currentUser$: BehaviorSubject<ICurrentUser | null> = new BehaviorSubject<ICurrentUser | null>(null);
  public readonly currentUser$: Observable<ICurrentUser | null> = this._currentUser$.asObservable();
  private readonly _activeCompany$: BehaviorSubject<ICompany | null> = new BehaviorSubject<ICompany | null>(null);
  public readonly activeCompany$: Observable<ICompany | null> = this._activeCompany$.asObservable();
  private readonly _activeRole$: BehaviorSubject<TCompanyRole | null> = new BehaviorSubject<TCompanyRole | null>(null);
  public readonly activeRole$: Observable<TCompanyRole | null> = this._activeRole$.asObservable();
  private readonly _activeLicense$: BehaviorSubject<ILicense | null> = new BehaviorSubject<ILicense | null>(null);
  public readonly activeLicense$: Observable<ILicense | null> = this._activeLicense$.asObservable();
  private readonly _activeCloud$: BehaviorSubject<ICloud | null> = new BehaviorSubject<ICloud | null>(null);
  public readonly activeCloud$: Observable<ICloud | null> = this._activeCloud$.asObservable();
  private readonly _activeGroup$: BehaviorSubject<IGroup | null> = new BehaviorSubject<IGroup | null>(null);
  public readonly activeGroup$: Observable<IGroup | null> = this._activeGroup$.asObservable();
  private readonly _quickAccessClouds$: BehaviorSubject<ICloud[]> = new BehaviorSubject<ICloud[]>([]);
  public readonly quickAccessClouds$: Observable<ICloud[]> = this._quickAccessClouds$.asObservable();
  private readonly _quickAccessGroups$: BehaviorSubject<IGroup[]> = new BehaviorSubject<IGroup[]>([]);
  public readonly quickAccessGroups$: Observable<IGroup[]> = this._quickAccessGroups$.asObservable();
  private readonly _quickAccessReceivers$: BehaviorSubject<IReceiver[]> = new BehaviorSubject<IReceiver[]>([]);
  public readonly quickAccessReceivers$: Observable<IReceiver[]> = this._quickAccessReceivers$.asObservable();
  private readonly _activeReceiver$: BehaviorSubject<IReceiver | null> = new BehaviorSubject<IReceiver | null>(null)
  public readonly activeReceiver$: Observable<IReceiver | null> = this._activeReceiver$.asObservable();
  private readonly _pendingJoinRequest$: BehaviorSubject<IJoinRequest | null> = new BehaviorSubject<IJoinRequest | null>(null);
  public readonly pendingJoinRequest$: Observable<IJoinRequest | null> = this._pendingJoinRequest$.asObservable();

  public setCurrentUser(currentUser: ICurrentUser): void {
    this._currentUser$.next(currentUser);
  }

  public setActiveCompany(company: ICompany | null): void {
    this._activeCompany$.next(company);
  }

  public setActiveRole(role: TCompanyRole | null): void {
    this._activeRole$.next(role);
  }

  public getActiveRole(): TCompanyRole | null {
    return this._activeRole$.getValue();
  }

  public setActiveLicense(license: ILicense | null): void {
    this._activeLicense$.next(license);
  }

  public getActiveCompany(): ICompany | null {
    return this._activeCompany$.getValue();
  }

  public getActiveLicense(): ILicense | null {
    return this._activeLicense$.getValue();
  }

  public setActiveCloud(cloud: ICloud | null): void {
    this._activeCloud$.next(cloud);
  }

  public setActiveGroup(group: IGroup | null): void {
    this._activeGroup$.next(group);
  }

  public getCurrentUser(): ICurrentUser | null {
    return this._currentUser$.getValue();
  }

  public getActiveCloud(): ICloud | null {
    return this._activeCloud$.getValue();
  }

  public getActiveGroup(): IGroup | null {
    return this._activeGroup$.getValue();
  }

  public getQuickAccessClouds(): ICloud[] {
    return this._quickAccessClouds$.getValue();
  }

  public getQuickAccessGroups(): IGroup[] {
    return this._quickAccessGroups$.getValue();
  }

  public setQuickAccessClouds(clouds: ICloud[]): void {
    this._quickAccessClouds$.next(clouds);
  }

  public setQuickAccessGroups(groups: IGroup[]): void {
    this._quickAccessGroups$.next(groups);
  }

  public setActiveReceiver(receiver: IReceiver | null): void {
    this._activeReceiver$.next(receiver);
  }

  public getActiveReceiver(): IReceiver | null {
    return this._activeReceiver$.getValue();
  }

  public getQuickAccessReceivers(): IReceiver[] {
    return this._quickAccessReceivers$.getValue();
  }

  public setQuickAccessReceivers(receivers: IReceiver[]): void {
    this._quickAccessReceivers$.next(receivers);
  }

  public addQuickAccessReceiver(receiver: IReceiver): void {
    const currentReceivers = this._quickAccessReceivers$.getValue();
    this._quickAccessReceivers$.next([...currentReceivers, receiver]);
  }

  public setPendingJoinRequest(joinRequest: IJoinRequest | null): void {
    this._pendingJoinRequest$.next(joinRequest);
  }

  public getPendingJoinRequest(): IJoinRequest | null {
    return this._pendingJoinRequest$.getValue();
  }

  public removeQuickAccessReceiver(receiverId: string): void {
    const currentReceivers = this._quickAccessReceivers$.getValue();
    const updatedReceivers = currentReceivers.filter(r => r.id !== receiverId);
    this._quickAccessReceivers$.next(updatedReceivers);
    
    // If the removed receiver was the active one, set next available or null
    const activeReceiver = this._activeReceiver$.getValue();
    if (activeReceiver?.id === receiverId) {
      this._activeReceiver$.next(updatedReceivers[0] ?? null);
    }
  }
}
